<template>
  <div class="pa-3">
    <v-btn class="mr-2 mb-2 pull-right" outlined small @click="handleCopyClick">
      <v-icon class="mr-2 left" size="15">far fa-clipboard</v-icon>
      Copy
    </v-btn>

    <div v-if="hasMaritimeAccess" class="hyp-type-container">
      <div
        class="pull-left text-center hyp-type-btn hyp-type-btn-left"
        :class="{
          'hyp-type-btn-selected': selectedType === 'political',
        }"
        @click="toggleType('political')"
      >
        Political
      </div>
      <div
        class="pull-left text-center hyp-type-btn hyp-type-btn-right"
        :class="{
          'hyp-type-btn-selected': selectedType === 'maritime',
        }"
        @click="toggleType('maritime')"
      >
        Maritime
      </div>
      <div class="clearfix"></div>
    </div>

    <div class="darkTheme clearfix">
      <div>
        <div
          v-if="!loading && (!items || (items && !items.length))"
          class="no-results-text pa-2"
        >
          No results found.
        </div>

        <DetailsListCluster
          v-if="items && items.length"
          @get-data="getContent"
          theme="dark"
          :items="items"
          :page="page"
          :pageSize="pageSize"
          :height-percent="90"
          :matomo-category="matomoCategory"
          :show-primary-topics="false"
          :show-thumbs-down="false"
          :scroll-to-top="scrollToTop"
          :loader="loading"
          @loaded="loading = false"
        ></DetailsListCluster>
      </div>
    </div>
    <v-overlay :absolute="true" :value="screenShotting">
      <div class="copy-overlay-indicator">
        <v-icon size="50" color="white">fas fa-check</v-icon>
        <div><strong>Copied</strong></div>
      </div>
    </v-overlay>
  </div>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashGet from "lodash/get";

import { mapGetters } from "vuex";

import { MODULE_PREFIX_HOME } from "@/store";
import {
  getBoundsStringFromGeometrySWNE,
  getCountyByCode,
} from "@/common/maps/utilities";
import { setInfiniteScrollState } from "@/common/basic.utilities";
import { getIncidents } from "@/services/controlRisks/incidents.service";
import { dataToCopy } from "@/services/search/wod.service";
import { hasMaritimeAccess } from "@/services/user/user.service";

import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  CLICK_MINIMIZE: "click-minimize",
  CLICK_CLOSE: "click-close",
};

export default {
  name: "IncidentsContent",
  components: {
    DetailsListCluster: () =>
      import("@/components/common/details/DetailsListCluster"),
  },
  props: {
    //iso3
    countryIso: {
      type: String,
      required: false,
    },
    countyCode: {
      type: String,
    },
    timeRange: {
      type: Array,
    },
    height: {
      type: String,
      default: "90vh",
    },
    refresh: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      screenShotting: false,
      loading: false,
      items: null,
      itemsResults: null,
      page: 0,
      pageSize: 25,
      requestTimestamp: new Date().toISOString(),
      getDataParams: {},
      scrollToTop: new Date().toISOString(),
      selectedType: "political",
      matomoCategory: EVENT_CATEGORIES.verified_events,
      hasMaritimeAccess: hasMaritimeAccess(),
    };
  },
  computed: {
    ...mapGetters({
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
    }),
    politicalEnabled() {
      return this.selectedType === "political";
    },
    maritimeEnabled() {
      return this.selectedType === "maritime" && this.hasMaritimeAccess;
    },
  },
  mounted() {
    this.getContent();
  },
  methods: {
    toggleType(type) {
      this.selectedType = type;
      this.page = 0;
      this.getContent();
    },
    getContent($state) {
      if (!this.page) {
        this.scrollToTop = new Date().toISOString();
      }
      this.getContentDebounced($state);
    },
    getContentDebounced: lodashDebounce(async function ($state) {
      let result;
      let dataParams;
      const page = !this.page ? 1 : this.page + 1;

      try {
        this.loading = true;

        dataParams = (await this.getParams()) || {};

        const PAGE_OFFSET = (page - 1) * this.pageSize;

        dataParams.pageSize = this.pageSize;
        dataParams.offset = PAGE_OFFSET;

        const data = await getIncidents(dataParams);

        if (lodashGet(data, "features")) {
          result = data.features;
          if (
            this.itemsResults &&
            Array.isArray(this.itemsResults) &&
            this.itemsResults.length &&
            page !== 1
          ) {
            this.itemsResults = this.itemsResults.concat(result);
          } else {
            this.itemsResults = result;
          }
          this.items = lodashCloneDeep(this.itemsResults);
        } else {
          this.itemsResults = null;
        }
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        if (result) {
          setInfiniteScrollState($state, { result: result }, "result");
          this.page = page;
        }
        this.loading = false;
        result = null;
        dataParams = {};
      }
    }, 500),
    async getParams(params) {
      if (!params) {
        params = {};
      }
      if (this.politicalEnabled) {
        params.politicalEnabled = this.politicalEnabled;
      }
      if (this.maritimeEnabled) {
        params.maritimeEnabled = this.maritimeEnabled;
      }
      if (this.countryIso) {
        params.aoiId = this.countryIso;
      }
      if (this.countyCode) {
        let county = await getCountyByCode(this.countyCode);
        if (county) {
          county = JSON.parse(county);
          if (lodashGet(county, "data.geometry.coordinates")) {
            params.bounds = getBoundsStringFromGeometrySWNE(
              county.data.geometry
            );
          }
        }
      }
      if (
        this.timeRange &&
        Array.isArray(this.timeRange) &&
        this.timeRange.length === 2
      ) {
        params.timeRange = this.timeRange;
        params.start = dayjs()
          .utc()
          .subtract(Math.abs(this.timeRange[0]), "days")
          .startOf("day")
          .toISOString();
        params.end = dayjs()
          .utc()
          .subtract(Math.abs(this.timeRange[1]), "days")
          .endOf("day")
          .toISOString();
      }

      return params;
    },
    handleClickClose(type) {
      this.$emit(EMITTED_ACTIONS.CLICK_CLOSE, type);
    },
    handleClickMinimize() {
      this.$emit(EMITTED_ACTIONS.CLICK_MINIMIZE);
    },
    async handleCopyClick() {
      //uncomment eventType to add to copy feature
      const colsToCopy = [
        "occurred",
        "title",
        //"eventType",
        "severity",
        "summary",
        "countries",
      ];

      document.activeElement.blur();

      this.screenShotting = true;

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.control_risk_events_verified,
        EVENT_ACTIONS.clicked,
        "Copy To Clipboard"
      );

      await dataToCopy(this.items, colsToCopy, false);

      setTimeout(() => {
        this.screenShotting = false;
      }, 500);
    },
  },
  watch: {
    refresh: {
      handler() {
        this.page = 0;
        this.getContent();
      },
    },
    countryIso: {
      handler() {
        this.page = 0;
        this.getContent();
      },
    },
    countyCode: {
      handler() {
        this.page = 0;
        this.getContent();
      },
    },
  },
};
</script>

<style scoped lang="scss">
.hyp-header {
  font-size: 24px;
  vertical-align: text-bottom;
}
.no-results-text {
  color: rgba(0, 0, 0, 0.87);
}
.darkTheme .no-results-text {
  color: white;
}
.hyp-header-row {
  height: 24px;
}
.content-container {
  height: 90vh;
  width: 100% !important;
  overflow-y: auto;
  padding: 2px 10px 0 0;
  margin-bottom: 20px;
}
.v-overlay {
  left: 0px !important;
  background-color: rgba(0, 0, 0, 0) !important;
}
.v-overlay--active.v-overlay {
  background-color: rgba(0, 0, 0, 0.75) !important;
}
.copy-overlay-indicator {
  width: 120px;
  height: 120px;
  background-color: green;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  font-size: large;
}
.hyp-type-container {
  width: 120px;
  font-size: 12px;
  position: relative;
  top: 2px;
}
.hyp-type-btn {
  color: white;
  border: 1px solid #7f7f7f;
  padding: 2px 0;
  cursor: pointer;
  width: 50%;
}
.hyp-type-btn-left {
  border-right: none;
  border-radius: 4px 0 0 4px;
}
.hyp-type-btn-right {
  border-radius: 0 4px 4px 0;
}
.hyp-type-btn-selected {
  background-color: $primary-color;
}
</style>
